import { type RefCallback, useCallback, useEffect, useRef } from "react";

import { isElectron } from "./env";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { useSettings } from "./hooks/useSettings";

export const RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY = "(max-width: 980px)";

export const RIGHT_PANEL_INLINE_SIDEBAR_WIDTH_STORAGE_KEY = "chat_right_panel_sidebar_width";
export const RIGHT_PANEL_INLINE_LEGACY_DEFAULT_WIDTH = 17 * 16;
// The legacy source control minimum, now the floor for every tab: the strip and
// its `+` still have to fit, and the changes list was already unusable narrower.
export const RIGHT_PANEL_INLINE_SIDEBAR_MIN_CONTENT_WIDTH = 17 * 16;
// Keep in sync with --app-window-resize-edge-inset in index.css. The right
// panel reserves this space inside its border box in Electron, so the outer
// min width needs to include it for the visible content area to match the left.
const RIGHT_PANEL_INLINE_ELECTRON_RESIZE_EDGE_INSET_WIDTH = 6;
export const RIGHT_PANEL_INLINE_SIDEBAR_MIN_WIDTH =
  RIGHT_PANEL_INLINE_SIDEBAR_MIN_CONTENT_WIDTH +
  (isElectron ? RIGHT_PANEL_INLINE_ELECTRON_RESIZE_EDGE_INSET_WIDTH : 0);
// The sidebar is one draggable slot shared by every tab, so it carries one
// width and one storage key, and switching tabs never resizes it.
export const RIGHT_PANEL_RAIL_WIDTH = 330;
export const RIGHT_PANEL_INLINE_DEFAULT_WIDTH = `${
  RIGHT_PANEL_RAIL_WIDTH + (isElectron ? RIGHT_PANEL_INLINE_ELECTRON_RESIZE_EDGE_INSET_WIDTH : 0)
}px`;
export const RIGHT_PANEL_INLINE_SIDEBAR_MAX_WIDTH = 256 * 16;
// Dragging the panel wider stops before the composer is narrower than this
// plus its fixed right-hand buttons: the room its model picker (or a waiting
// question's answer field) keeps on the left.
export const COMPOSER_COMPACT_MIN_LEFT_CONTROLS_WIDTH_PX = 208;

// Both right-panel sheets start below the chat header (measured into
// --chat-header-bottom, which already accounts for any titlebar) so the
// header's toggles stay visible and interactive while a panel is open. The
// popups re-enable pointer events because their shared viewport disables
// them to let presses over the header reach the header.
const RIGHT_PANEL_SHEET_BELOW_HEADER_CLASS_NAME =
  "pointer-events-auto mt-[var(--chat-header-bottom)] h-[calc(100%-var(--chat-header-bottom))] max-h-[calc(100%-var(--chat-header-bottom))] p-0";
// Diff sheet width tiers: desktop keeps the fixed panel, the 640-760px band
// keeps the near-full 88vw sheet (disjoint `sm:max-[760px]:` so no cascade
// ordering is relied on), and phones go full width.
export const RIGHT_PANEL_SHEET_CLASS_NAME = `${RIGHT_PANEL_SHEET_BELOW_HEADER_CLASS_NAME} w-[min(42vw,28rem)] min-w-80 max-w-[28rem] sm:max-[760px]:w-[min(88vw,24rem)] sm:max-[760px]:min-w-0 max-sm:w-full max-sm:min-w-0 max-sm:max-w-none`;
// The rail overlays as a partial sheet: 400px where it fits, and as much of
// the viewport as fits below that, so a slice of the conversation stays
// visible and tappable to dismiss.
export const RIGHT_PANEL_RAIL_SHEET_CLASS_NAME = `${RIGHT_PANEL_SHEET_BELOW_HEADER_CLASS_NAME} w-[min(92vw,var(--right-panel-rail-width))] max-w-[var(--right-panel-rail-width)]`;
export const RIGHT_PANEL_SHEET_VIEWPORT_CLASS_NAME = "pointer-events-none";
export const RIGHT_PANEL_SHEET_BACKDROP_CLASS_NAME = "mt-[var(--chat-header-bottom)]";

export const CHAT_HEADER_BOTTOM_CSS_VAR = "--chat-header-bottom";
export const RIGHT_PANEL_INSET_CSS_VAR = "--right-panel-inset";

/**
 * Publishes how much of the viewport's right edge the open right panel occupies
 * as a root-level CSS variable, so body-portaled overlays can stay clear of it.
 * `0px` while the panel is closed. Attach the returned ref to the element that
 * holds the panel's layout slot.
 *
 * Same pattern (and the same reason) as {@link useChatHeaderBottomVarRef}: the
 * toast viewport is fixed to the viewport, not to the chat column, so nothing in
 * the layout can push it aside. The width changes on open/close, on a drag of
 * the panel's resize handle, and on a window resize, all of which the observer
 * catches. The variable is reset on unmount so a route without a right panel
 * does not leave overlays inset for a panel that is gone.
 */
export function useRightPanelInsetVarRef(): RefCallback<HTMLElement> {
  return useCallback((node: HTMLElement | null) => {
    const rootStyle = document.documentElement.style;
    if (!node || typeof ResizeObserver === "undefined") {
      rootStyle.setProperty(RIGHT_PANEL_INSET_CSS_VAR, "0px");
      return undefined;
    }
    const publish = () => {
      rootStyle.setProperty(
        RIGHT_PANEL_INSET_CSS_VAR,
        `${Math.max(0, Math.round(node.getBoundingClientRect().width))}px`,
      );
    };
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    publish();
    return () => {
      observer.disconnect();
      rootStyle.setProperty(RIGHT_PANEL_INSET_CSS_VAR, "0px");
    };
  }, []);
}

/**
 * Publishes the chat header's bottom edge (in viewport px) as a root-level CSS
 * variable so body-portaled overlays can align to it. Attach the returned ref
 * to the chat header element.
 *
 * The value is a viewport-relative position, so it must be re-measured when
 * the header moves, not just when it resizes: mobile browsers scroll the
 * layout viewport to reveal a focused input under the on-screen keyboard, and
 * a publish captured in that state would otherwise stick until the next
 * header resize. Clamping keeps a mid-scroll measurement from pulling the
 * overlays above the viewport, and the last value survives header unmounts
 * (loading states, thread switches) so an open sheet doesn't snap to the top
 * before the header remounts and republishes.
 */
export function useChatHeaderBottomVarRef(): RefCallback<HTMLElement> {
  return useCallback((node: HTMLElement | null) => {
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const rootStyle = document.documentElement.style;
    const publish = () => {
      rootStyle.setProperty(
        CHAT_HEADER_BOTTOM_CSS_VAR,
        `${Math.max(0, node.getBoundingClientRect().bottom)}px`,
      );
    };
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    const viewport = window.visualViewport;
    window.addEventListener("scroll", publish, { passive: true });
    viewport?.addEventListener("resize", publish);
    viewport?.addEventListener("scroll", publish);
    publish();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", publish);
      viewport?.removeEventListener("resize", publish);
      viewport?.removeEventListener("scroll", publish);
    };
  }, []);
}

/**
 * Memory key for a draft route's sidebar state, distinct from the
 * `environmentId:threadId` keys server threads use. The draft route and the
 * chat header resolve the same draft through this so they agree.
 */
export function draftRightPanelStateKey(draftId: string): string {
  return `draft:${draftId}`;
}

/**
 * Whether a thread with no sidebar state at all should open on Source. The
 * setting only applies on wide layouts: in sheet mode the sidebar covers the
 * conversation, so threads there always start closed.
 */
export function useRightPanelDefaultVisible(): boolean {
  const sheetLayout = useMediaQuery(RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY);
  const defaultOpenSetting = useSettings((settings) => settings.sourceControlPanelDefaultOpen);
  return defaultOpenSetting && !sheetLayout;
}

export function normalizeRightPanelStoredWidth(width: number) {
  return width <= RIGHT_PANEL_INLINE_LEGACY_DEFAULT_WIDTH ? null : width;
}

/**
 * An explicit `=1` carried into a narrow layout (or across a thread switch)
 * closes itself, so a sheet never lands on top of the conversation the user
 * navigated to. Shared by both right-panel sheets.
 */
export function useAutoHideRightPanelSheet(input: {
  enabled: boolean;
  resetKey: string | null;
  /** The raw explicit URL value for the panel, not the resolved boolean. */
  panelState: "1" | "0" | undefined;
  blocked?: boolean;
  onAutoHide: () => void;
}): boolean {
  const { blocked, enabled, onAutoHide, resetKey, panelState } = input;
  const previousRef = useRef({
    enabled: false,
    resetKey: null as string | null,
  });
  const shouldAutoHide =
    enabled &&
    !blocked &&
    panelState === "1" &&
    (!previousRef.current.enabled || resetKey !== previousRef.current.resetKey);

  useEffect(() => {
    previousRef.current = {
      enabled,
      resetKey,
    };

    if (!shouldAutoHide) {
      return;
    }

    onAutoHide();
  }, [enabled, onAutoHide, resetKey, shouldAutoHide]);

  return shouldAutoHide;
}
